import { MedplumClient, resolveId } from "@medplum/core";

import { Status as EncounterStatus } from "@/encounters/zod-schemas/encounters";
import { fetchPregnancyRecords } from "@/pregnancy";
import { fetchAllEpisodeOfCareByClientId } from "@/services/episode-of-care";
import {
  getOrganizationReference,
  getResourcesFromBundle,
  joinReferenceString,
} from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";
import { fetchAllPaginatedResources } from "@/utils/pagination";

import { findPregnancyRecordForEncounter } from "../utils/encounter-helpers";

const PAGE_SIZE = 20;

function getEncountersQueryForExport(clientId: string, practiceId?: string) {
  return {
    subject: joinReferenceString("Patient", clientId),
    status: `${EncounterStatus.Finished},${EncounterStatus.Cancelled}`,
    _sort: "-date",
    ...(practiceId ? { _compartment: getOrganizationReference(practiceId) } : {}),
  };
}

export async function fetchAllEncountersForExport(
  medplum: MedplumClient,
  clientId: string,
  practiceId?: string,
) {
  const result = await fetchAllPaginatedResources(medplum, "Encounter", {
    ...getEncountersQueryForExport(clientId, practiceId),
    _elements: "id",
  });
  if (!result.success) {
    showSystemErrorNotification();
    return [];
  }

  return result.data.map((encounter) => encounter.id).filter((id): id is string => Boolean(id));
}

export async function fetchEncountersForExport(
  medplum: MedplumClient,
  clientId: string,
  page: number,
  practiceId?: string,
) {
  const { bundle: encountersBundle } = await medplum.searchResources("Encounter", {
    ...getEncountersQueryForExport(clientId, practiceId),
    _elements: "id,serviceType,period,episodeOfCare,type,class,reasonCode",
    _count: PAGE_SIZE,
    _offset: (page - 1) * PAGE_SIZE,
    _total: "accurate",
  });
  const encounters = getResourcesFromBundle("Encounter", encountersBundle);

  // Fetch all pregnancy records (current and historical) to determine per-encounter pregnancy
  // status. An encounter is only considered "pregnant" if it is directly linked to a pregnancy
  // record via List.entry — not based on the patient's current active condition.
  const pregnancyRecords = await fetchPregnancyRecords(medplum, clientId, {
    status: "current,retired",
  });

  const episodesOfCare = await fetchAllEpisodeOfCareByClientId(medplum, clientId);

  const data = encounters.map((encounter) => {
    const episodeOfCare = episodesOfCare.find((episodeOfCareItem) =>
      encounter.episodeOfCare?.some(
        (episodeReference) => resolveId(episodeReference) === episodeOfCareItem.id,
      ),
    ); // we're always getting the first episode of care for the encounter as we're only defining one episode of care for the encounter

    const linkedPregnancyRecord = findPregnancyRecordForEncounter(pregnancyRecords, encounter.id!);

    return {
      encounter,
      episodeOfCare,
      isPregnantDuringEncounter: linkedPregnancyRecord !== undefined,
    };
  });

  return { data, total: Math.ceil((encountersBundle.total ?? 0) / PAGE_SIZE) };
}
